import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PhotoAlbum from "react-photo-album";
import AutoSizer from "react-virtualized-auto-sizer";
import { toast } from "react-toastify";
import EmojiPicker from "emoji-picker-react";
import axios from "axios";
import { debounce, get } from "lodash";
import Cookies from "js-cookie";
import { useParams, useNavigate, Link } from "react-router-dom";
import BaseUrl from "../../Components/BaseUrl";

import { Download } from "@mui/icons-material";
const DEFAULT_PROFILE_IMAGE = "/default-profile.png";
const POLLING_INTERVAL = 3000;

const getColorFromName = (name) => {
  if (!name) return { backgroundColor: "hsl(0, 0%, 50%)" };

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;
  const saturation = 70 + (Math.abs(hash) % 30); // 70–99%
  const lightness = 50 + (Math.abs(hash) % 10);

  return {
    backgroundColor: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
  };
};

const formatTimeFromTimestamp = (timestamp) => {
  if (!timestamp || !(timestamp instanceof Date) || isNaN(timestamp))
    return "--:--";

  let hours = timestamp.getHours();
  const minutes = timestamp.getMinutes();
  const period = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;

  return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const ImageWithLoading = ({ src, alt, onClick, ...props }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Skip loading if src is empty or invalid
    if (!src) {
      setIsLoading(false);
      setHasError(true);
      return;
    }

    const img = new Image();
    img.src = src;
    img.onload = () => setIsLoading(false);
    img.onerror = () => {
      setIsLoading(false);
      setHasError(true);
    };

    // Ensure minimum loader visibility
    const timer = setTimeout(() => {}, 500);

    return () => {
      clearTimeout(timer);
      // No need to revoke Blob URL here; handled in handleSendMessage
    };
  }, [src]);

  return (
    <div className="relative w-full h-[110px]">
      {isLoading && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
          <div
            className="w-6 h-6 border-4 border-t-4 border-gray-500 border-solid rounded-full animate-spin"
            style={{ borderTopColor: "transparent" }}
          ></div>
        </div>
      )}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 rounded-lg">
          <div className="w-6 h-6 text-red-500">⚠️</div>
        </div>
      )}
      <img
        src={src || "/path/to/fallback-image.jpg"} // Fallback if src is invalid
        alt={alt}
        className={`w-full h-[110px] object-contain rounded-lg ${
          isLoading && !hasError ? "opacity-0" : "opacity-100"
        }`}
        loading="lazy"
        onError={(e) => {
          setIsLoading(false);
          setHasError(true);
          e.target.src = "/path/to/fallback-image.jpg"; // Replace with your fallback image path
        }}
        onClick={onClick}
        {...props}
      />
    </div>
  );
};

const Message = React.memo(({ message, isOwn, openImageModal }) => {
  const profileImage = message.user_profile || DEFAULT_PROFILE_IMAGE;
  return (
    <div
      className={`flex mb-2 ${isOwn ? "justify-end mr-2" : "justify-start"}`}
    >
      {!isOwn && (
        <div className="mr-2 mt-1 flex-shrink-0">
          {message.user_profile ? (
            <img
              src={profileImage}
              alt="Profile"
              className="w-8 h-8 rounded-full object-cover"
              onError={(e) => {
                e.target.src = DEFAULT_PROFILE_IMAGE;
              }}
            />
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={getColorFromName(message.user_name || "?")}
            >
              {(message.user_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      )}

      <div
        className={`flex flex-col ${
          isOwn ? "items-end " : "items-start"
        } max-w-[80%]`}
      >
        <div
          className={`relative px-3 py-2 rounded-lg break-words  ${
            isOwn ? "bg-[#DCF8C6] rounded-tr-none" : "bg-white rounded-tl-none"
          }`}
        >
          {message.files?.length > 0 && (
            <div className="mt-3 flex flex-col gap-4 ">
              {message.files.some((f) => f.isImage || f.isVideo) &&
                (() => {
                  const mediaFiles = message.files.filter(
                    (file) => file.isImage
                  );
                  const imageFiles = message.files.filter(
                    (file) => file.isImage
                  );
                  const videoFiles = message.files.filter((f) => f.isVideo);
                  const photos = mediaFiles.map((file) => ({
                    src: file.url,
                    width: file.width || 4,
                    height: file.height || 3,
                  }));
                  return (
                    <div className="">
                      <div className="max-w-[260px] space-y-2">
                        {imageFiles.length > 0 && (
                          <PhotoAlbum
                            layout="rows"
                            photos={photos}
                            columns={(containerWidth) =>
                              Math.min(2, imageFiles.length)
                            }
                            targetRowHeight={110}
                            spacing={6}
                            renderPhoto={({ photo, index }) => (
                              <ImageWithLoading
                                src={photo.src}
                                alt={`Image ${index}`}
                                onClick={() =>
                                  openImageModal(imageFiles, index)
                                }
                              />
                            )}
                            onClick={({ index }) =>
                              openImageModal(imageFiles, index)
                            }
                          />
                        )}

                        {/* Video Grid */}
                        {videoFiles.length > 0 && (
                          <div
                            className={`grid gap-3 ${
                              videoFiles.length === 1
                                ? "grid-cols-1"
                                : "grid-cols-2"
                            }`}
                          >
                            {videoFiles.map((file, idx) => (
                              <video
                                key={idx}
                                src={file.url}
                                controls
                                className="w-[358px] h-auto object-cover rounded-lg"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {message.files.some((f) => f.isDocument) && (
                <div className="flex flex-col gap-2">
                  {message.files
                    .filter((f) => f.isDocument)
                    .map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-gray-100 px-3 py-2 rounded-md w-full sm:w-[300px]"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <i className="fas fa-file-alt text-gray-500"></i>
                          <span className="truncate text-sm">{file.name}</span>
                        </div>
                        <a
                          href={file.url}
                          download
                          className="text-blue-600 text-sm underline hover:text-blue-800"
                        >
                          <Download />
                        </a>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {message.message && (
            <div className="text-sm my-1">{message.message}</div>
          )}

          <div className="flex justify-end items-center mt-0.5">
            <span className="text-[10px] text-gray-500">
              {formatTimeFromTimestamp(message.timestamp)}
            </span>
          </div>
        </div>
      </div>

      {isOwn && (
        <img
          src={profileImage}
          alt="Profile"
          className="w-8 h-8 rounded-full ml-2 mt-1"
          onError={(e) => {
            e.target.src = DEFAULT_PROFILE_IMAGE;
          }}
        />
      )}
    </div>
  );
});

const Messaging = () => {
  const { user_slug } = useParams();
  const navigate = useNavigate();

  // State management
  const [profileData, setProfileData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [messages, setMessages] = useState({});
  const [currentUserSlug, setCurrentUserSlug] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileCaption, setFileCaption] = useState("");
  const [searchText, setSearchText] = useState("");
  const [imageModalData, setImageModalData] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [lastMessages, setLastMessages] = useState({});
  const [lastContactUpdate, setLastContactUpdate] = useState({});
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });

  // Refs
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const textareaRef = useRef(null);
  const hasInitialScrollDone = useRef(false);
  const isMounted = useRef(true);
  const messagesEndRef = useRef();

  const scrollPositionRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const isInitialLoadRef = useRef(true);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        if (!event.target.closest(".emoji-button")) {
          setShowEmojiPicker(false);
        }
      }

      if (
        showAttachmentMenu &&
        attachmentMenuRef.current &&
        !attachmentMenuRef.current.contains(event.target)
      ) {
        if (!event.target.closest(".attachment-button")) {
          setShowAttachmentMenu(false);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker, showAttachmentMenu]);

  const getCurrentuser = async () => {
    const token = Cookies.get("token");
    if (!token) {
      toast.error("Please log in to continue");
      return;
    }
    try {
      const result = await axios.get(`${BaseUrl}getCurrentUser`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.data.status === 200) {
        setProfileData(result.data.data);
      } else {
        toast.error("Failed to load contacts");
      }
    } catch (error) {
      toast.error("Failed to load contacts");
    }
  };

  const getUsers = useCallback(async () => {
    const token = Cookies.get("token");
    if (!token) {
      toast.error("Please log in to continue");
      return;
    }
    setIsLoading(true);
    try {
      const result = await axios.get(`${BaseUrl}messageUsers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result.data.status === 200) {
        setContacts(result.data.data);

        setFilteredContacts(result.data.data);
      } else {
        toast.error("Failed to load contacts");
      }
    } catch (error) {
      toast.error("Failed to load contacts");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // const fetchMessages = useCallback(async (slug) => {
  //   const token = Cookies.get("token");
  //   if (!token || !slug) return;

  //   try {
  //     const result = await axios.get(`${BaseUrl}getChats/${slug}`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });
  //     console.log(result.data.data.messages.data,"messages");
  //     if (result.data.status === 200 && Array.isArray(result.data.data.messages.data)) {
  //       return result.data.data.messages.data.map((msg) => {
  //         const updatedFiles = (msg.files || []).map((file) => ({
  //           ...file,
  //           isImage: file.type === "image",
  //           isVideo: file.type === "video",
  //           isDocument: !["image", "video"].includes(file.type),
  //         }));
  //         const localTimestamp = msg.timestamp
  //           ? new Date(msg.timestamp.replace(" ", "T") + "Z")
  //           : msg.timestamp;
  //         return {
  //           ...msg,
  //           files: updatedFiles,
  //           timestamp: localTimestamp,
  //         };
  //       });
  //     }
  //     return [];
  //   } catch (error) {
  //     console.error("Error fetching messages:", error);
  //     return [];
  //   }
  // }, []);

  const fetchMessages = useCallback(async (slug, page = 1) => {
    console.log(page,"page");
    const token = Cookies.get("token");
    if (!token || !slug) return { messages: [], pagination: null };
  
    try {
      const result = await axios.get(`${BaseUrl}getChats/${slug}?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (result.data.status === 200 && Array.isArray(result.data.data.messages.data)) {
        const formattedMessages = result.data.data.messages.data.map((msg) => {
          const updatedFiles = (msg.files || []).map((file) => ({
            ...file,
            isImage: file.type === "image",
            isVideo: file.type === "video",
            isDocument: !["image", "video"].includes(file.type),
          }));
          const localTimestamp = msg.timestamp
            ? new Date(msg.timestamp.replace(" ", "T") + "Z")
            : msg.timestamp;
          return {
            ...msg,
            files: updatedFiles,
            timestamp: localTimestamp,
          };
        });
  
        return {
          messages: formattedMessages,
          pagination: {
            current_page: result.data.data.current_page,
            last_page: result.data.data.messages.last_page,
          },
         
        };
      }
      return { messages: [], pagination: null };
    } catch (error) {
      console.error("Error fetching messages:", error);
      return { messages: [], pagination: null };
    }
  }, []);


  // const handleScroll = useCallback(() => {
  //   if (!listRef.current || isLoadingMessages || !hasMoreMessages) return;
  //   const { scrollTop, scrollHeight, clientHeight } = listRef.current;
  //   if (scrollTop < 800 && pagination.current_page < pagination.last_page) {
  //     loadMoreMessages();
  //   }
  // }, [isLoadingMessages, hasMoreMessages, pagination]);


  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMessages || !hasMoreMessages) return;
    setIsLoadingMessages(true);
    try {
      const listElement = listRef.current;
      const prevScrollHeight = listElement.scrollHeight;
      const prevScrollTop = listElement.scrollTop;

      const newPage = parseInt(pagination.current_page, 10) + 1;
      const { messages: newMessages, pagination: newPagination } = await fetchMessages(currentUserSlug, newPage);

      if (newMessages.length > 0) {
        setMessages(prev => ({
          ...prev,
          [currentUserSlug]: [...newMessages, ...(prev[currentUserSlug] || [])],
        }));
        setPagination(newPagination);
        if (newPagination.current_page >= newPagination.last_page) {
          setHasMoreMessages(false);
        }
        requestAnimationFrame(() => {
          const newScrollHeight = listElement.scrollHeight;
          listElement.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        });
      }
    } finally {
      setIsLoadingMessages(false);
    }
  }, [pagination, currentUserSlug, fetchMessages]);

  const currentMessages = currentUserSlug
  ? messages[currentUserSlug] || []
  : [];
  
//scrolling
  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    isAtBottomRef.current = checkIfAtBottom();
    
    // Load more messages when scrolling near top
    if (listRef.current.scrollTop < 100 && !isLoadingMessages && hasMoreMessages) {
      loadMoreMessages();
    }
  }, [isLoadingMessages, hasMoreMessages, loadMoreMessages]);  


  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => {
        listElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);


  const scrollToBottom = (behavior = 'smooth') => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: behavior
      });
      isAtBottomRef.current = true;
    }
  };
  
  const checkIfAtBottom = () => {
    if (!listRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    return scrollHeight - (scrollTop + clientHeight) < 50;
  };

  useEffect(() => {
    if (!listRef.current || !currentUserSlug) return;
  
    if (isAtBottomRef.current || isInitialLoadRef.current) {
      scrollToBottom(isInitialLoadRef.current ? 'auto' : 'smooth');
      isInitialLoadRef.current = false;
    }
  }, [currentMessages, currentUserSlug]);

  useEffect(() => {
    if (currentUserSlug) {
      isInitialLoadRef.current = true;
      isAtBottomRef.current = true;
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = listRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [currentUserSlug]);

  //scrolling

  useEffect(() => {
    if (!currentUserSlug) return;
    let isActive = true;
    let timeoutId;
    const pollData = async () => {
      if (!isMounted.current) return;
      try {
        const newMessages = await fetchMessages(currentUserSlug,1);
        const contactsResult = await axios.get(`${BaseUrl}messageUsers`, {
          headers: { Authorization: `Bearer ${Cookies.get("token")}` },
        });
        if (!isActive) return;
        setFilteredContacts((prevContacts) => {
          const updatedContacts = contactsResult.data.data.map((contact) => {
            const prevContact = prevContacts.find(
              (c) => c.user_slug === contact.user_slug
            );
            const hasNewMessage =
              prevContact &&
              prevContact.id !== contact.id &&
              contact.user_slug !== currentUserSlug;
            return {
              ...contact,
              hasNewMessage,
              highlight: hasNewMessage,
            };
          });
          return updatedContacts;
        });
    
        setMessages((prev) => {
          const currentMessages = prev[currentUserSlug] || [];
          const uniqueNewMessages = newMessages.messages.filter(
            (newMsg) => !currentMessages.some((msg) => msg.id === newMsg.id)
          );
          if (uniqueNewMessages.length === 0) return prev;
          return {
            ...prev,
            [currentUserSlug]: [...currentMessages, ...uniqueNewMessages].sort(
              (a, b) => a.timestamp - b.timestamp
            ),
          };
        });
      } catch (error) {
        console.error("Polling error:", error);
      } finally {
        if (isActive) {
          timeoutId = setTimeout(pollData, POLLING_INTERVAL);
        }
      }
    };
    pollData();
    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [currentUserSlug]);


  useEffect(() => {
    getUsers();
    return () => {
      isMounted.current = false;
    };
  }, [getUsers]);


  useEffect(() => {
    getCurrentuser();
  }, []);


  useEffect(() => {
    if (contacts.length > 0 && user_slug) {
      const contact = contacts.find((c) => c.user_slug === user_slug);
      if (contact) {
        handleContactSelect(contact.user_slug);
      } else {
        navigate("/user/messaging");
      }
    } else if (contacts.length > 0 && !currentUserSlug) {
      handleContactSelect(contacts[0].user_slug);
    }
  }, [user_slug, contacts, currentUserSlug]);


  useEffect(() => {
    if (!imageModalData) return;
    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowLeft":
          prevImage();
          break;
        case "ArrowRight":
          nextImage();
          break;
        case "Escape":
          closeImageModal();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [imageModalData, currentImageIndex]);


  const handleContactSelect = useCallback( async (userSlug, event) => {
      if (userSlug === currentUserSlug) return;
      setContacts((prev) =>
        prev.map((contact) => ({
          ...contact,
          highlight: contact.user_slug === userSlug ? false : contact.highlight,
        }))
      );
      setCurrentUserSlug(userSlug);
      setShowAttachmentMenu(false);
      setShowEmojiPicker(false);
      setSelectedFiles([]);
      setFileCaption("");
      hasInitialScrollDone.current = false;
      setIsLoadingMessages(true);
    try {
      const { messages: initialMessages, pagination: initialPagination } = await fetchMessages(userSlug,1);

      const safeMessages = Array.isArray(initialMessages) ? initialMessages : [];
      setMessages((prev) => ({
        ...prev,
        [userSlug]: safeMessages,
      }));
      if (initialPagination) {
        setPagination(initialPagination);
        setHasMoreMessages(initialPagination.current_page < initialPagination.last_page);
      }
    } finally {
      setIsLoadingMessages(false);
    }
    },
    [currentUserSlug, messages]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (value.length >= 3) {
      debouncedSearch(value);
    } else if (value.length === 0) {
      setFilteredContacts(contacts);
    }
  };


  const debouncedSearch = useMemo(
    () =>
      debounce(async (value) => {
        const token = Cookies.get("token");
        if (!token || !value.trim()) return;

        try {
          setIsLoading(true);
          const result = await axios.get(`${BaseUrl}searchUsers`, {
            params: { name: value },
            headers: { Authorization: `Bearer ${token}` },
          });

          if (result.data.status === 200) {
            setFilteredContacts(result.data.data);
          }
        } catch (error) {
          console.error("Search error:", error);
        } finally {
          setIsLoading(false);
        }
      }, 500),
    []
  );


  const handleSendMessage = async () => {
    const hasContent =
      newMessage.trim() || fileCaption.trim() || selectedFiles.length > 0;
    if (!hasContent || !currentUserSlug) return;

    const token = Cookies.get("token");
    if (!token) {
      toast.error("Please log in to continue");
      return;
    }

    const tempId = Date.now().toString();
    const timestamp = new Date();
    const finalMessage = newMessage.trim() || fileCaption.trim();

    const optimisticFiles = selectedFiles.map((file) => ({
      name: file.name,
      type: file.type,
      url:
        file.isImage || file.isVideo
          ? URL.createObjectURL(file.file)
          : file.url || file.name, // Use Blob URL for images/videos
      isImage: file.isImage,
      isVideo: file.isVideo,
      isDocument: file.isDocument,
    }));

    const optimisticMsg = {
      id: tempId,
      message: finalMessage,
      files: optimisticFiles,
      timestamp,
      isOwn: true,
      user_profile: profileData?.profile,
      user_name: profileData?.name,
      user_slug: profileData?.slug,
    };
    console.log("optimisticMsg", optimisticMsg);
    setMessages((prev) => ({
      ...prev,
      [currentUserSlug]: [...(prev[currentUserSlug] || []), optimisticMsg],
    }));
    setNewMessage("");
    setSelectedFiles([]);
    setFileCaption("");
    setShowEmojiPicker(false);
    setTimeout(() => {
      isAtBottomRef.current = true;
      scrollToBottom();
    }, 100);
    try {
      const formData = new FormData();
      formData.append("message", finalMessage);
      selectedFiles.forEach((file) =>
        formData.append(`attachment[]`, file.file)
      );
      formData.append("recipient_slug", currentUserSlug);
      const response = await axios.post(`${BaseUrl}sendMessages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data.status === 200) {
        optimisticFiles.forEach((file) => {
          if (file.url.startsWith("blob:")) {
            URL.revokeObjectURL(file.url);
          }
        });
        setMessages((prev) => ({
          ...prev,
          [currentUserSlug]: (prev[currentUserSlug] || []).map((msg) =>
            msg.id === tempId
              ? {
                  ...msg,
                  id: response.data.data.id,
                  files:
                    response.data.data.files?.map((serverFile) => ({
                      name: serverFile.name,
                      type: serverFile.type,
                      url: serverFile.url,
                      isImage: serverFile.type === "image",
                      isVideo: serverFile.type === "video",
                      isDocument: !["image", "video"].includes(serverFile.type),
                    })) || msg.files,
                }
              : msg
          ),
        }));
        console.log("response.data.data", response.data.data);

        const lastMessageText =
          finalMessage ||
          `Sent ${selectedFiles.length} file${
            selectedFiles.length > 1 ? "s" : ""
          }`;

        setLastMessages((prev) => ({
          ...prev,
          [currentUserSlug]: {
            message: lastMessageText,
            timestamp,
          },
        }));

        setContacts((prev) =>
          prev.map((contact) =>
            contact.user_slug === currentUserSlug
              ? { ...contact, message: lastMessageText }
              : contact
          )
        );
        setFilteredContacts((prev) =>
          prev.map((contact) =>
            contact.user_slug === currentUserSlug
              ? { ...contact, message: lastMessageText }
              : contact
          )
        );
      } else if (response.data.status === 500) {
        let errorMsg = "Failed to send your message.";

        const { message, data } = response.data;

        if (data && typeof data === "object") {
          const allErrors = Object.values(data).flat();

          const fileSizeError = allErrors.find((err) =>
            err.toLowerCase().includes("must not be greater than")
          );

          if (fileSizeError) {
            errorMsg =
              "One or more files are too large to upload. Max size allowed is 10MB.";
          } else {
            errorMsg = allErrors.join(" ");
          }
        } else if (message) {
          errorMsg = message;
        }

        toast.error(errorMsg);

        // Revert optimistic message
        setMessages((prev) => ({
          ...prev,
          [currentUserSlug]: (prev[currentUserSlug] || []).filter(
            (msg) => msg.id !== tempId
          ),
        }));
        optimisticFiles.forEach((file) => {
          if (file.url.startsWith("blob:")) {
            URL.revokeObjectURL(file.url);
          }
        });
        return;
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error sending message");

      setMessages((prev) => ({
        ...prev,
        [currentUserSlug]: (prev[currentUserSlug] || []).filter(
          (msg) => msg.id !== tempId
        ),
      }));
      optimisticFiles.forEach((file) => {
        if (file.url.startsWith("blob:")) {
          URL.revokeObjectURL(file.url);
        }
      });
    }
  };


  const MAX_FILES = 3;
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length || !currentUserSlug) return;

    if (selectedFiles.length + files.length > MAX_FILES) {
      toast.error(`You can only upload up to ${MAX_FILES} files.`);
      event.target.value = null; // Reset input
      return;
    }

    const newFiles = files.map((file) => ({
      name: file.name,
      type: file.type,
      url:
        file.type.startsWith("image/") || file.type.startsWith("video/")
          ? URL.createObjectURL(file)
          : null,
      file,
      isImage: file.type.startsWith("image/"),
      isVideo: file.type.startsWith("video/"),
      isDocument:
        !file.type.startsWith("image/") && !file.type.startsWith("video/"),
    }));

    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setShowAttachmentMenu(false);
    event.target.value = null;
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };


  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };


  const openImageModal = (files, index = 0) => {
    setImageModalData(files);
    setCurrentImageIndex(index);
  };


  const closeImageModal = () => {
    setImageModalData(null);
    setCurrentImageIndex(0);
  };


  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % imageModalData.length);
  };


  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + imageModalData.length) % imageModalData.length
    );
  };


  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px`;
    }
  }, [newMessage]);


  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file.url && file.url.startsWith("blob:")) {
          URL.revokeObjectURL(file.url);
        }
      });
    };
  }, [selectedFiles]);



  // useEffect(() => {
  //   if (currentUserSlug && messages[currentUserSlug]?.length && !hasInitialScrollDone.current) {
  //     messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  //     hasInitialScrollDone.current = true;
  //   }
  // }, [currentUserSlug, messages]);


  function getTimeAgo(dateString) {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}y`;
    if (months > 0) return `${months}mo`;
    if (weeks > 0) return `${weeks}w`;
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  }
  return (
    <div className="bg-[#F3F2EF] pt-20 h-screen !pb-0">
      <div className="flex flex-col lg:flex-row container h-full mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        {/* Left sidebar - contacts */}
        <div className="w-full xl:w-1/3 bg-white border-r border-gray-200 max-h-[750px]">
          <div className="p-[17px] border-b border-gray-200">
            <input
              type="text"
              placeholder="Search Messages or Users"
              className="w-full p-2 border rounded-lg"
              value={searchText}
              onChange={handleSearchChange}
            />
            {/* {isLoading && (
              <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )} */}
          </div>
          <div className="overflow-y-auto max-h-[330px] xl:max-h-[474px] w-full">
            {isLoading ? (
              <div className="animate-pulse">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="p-4 flex items-center border-b border-gray-200"
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredContacts.length === 0 && searchText ? (
              <div className="p-4 text-center text-gray-500">
                No users found
              </div>
            ) : (
              filteredContacts.map((contact) => (
                <Link
                  to={`/user/messaging/${contact.user_slug}`}
                  key={contact.user_slug}
                  className={`relative p-4 flex items-center border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition
      ${
        currentUserSlug === contact.user_slug
          ? "border-l-blue-400 border-l-[5px] bg-blue-100"
          : ""
      }
      ${contact.highlight ? "bg-blue-50 animate-pulse" : ""}`}
                  onClick={(e) => handleContactSelect(contact.user_slug, e)}
                >
                  {contact.user_profile ? (
                    <img
                      src={contact.user_profile}
                      alt={`Profile of ${contact.user_name}`}
                      className="w-10 h-10 rounded-full mr-3"
                    />
                  ) : (
                    <div
                      style={getColorFromName(contact.user_name || "?")}
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white mr-3 font-bold text-sm"
                    >
                      {(contact.user_name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}

                  {contact.isOnline && (
                    <span className="absolute left-12 top-11 ml-auto text-green-500">
                      <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
                    </span>
                  )}
                  <div className="flex-1">
                    <div className="font-bold flex justify-between items-center">
                      <span>{contact.user_name}</span>
                      {!contact.is_Own && contact.is_read === 0 && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      )}
                    </div>
                    <div
                      className={`text-sm truncate w-48 ${
                        contact.is_Own
                          ? "text-gray-500"
                          : contact.is_read === 0
                          ? "font-semibold text-gray-800"
                          : "text-gray-500"
                      }`}
                    >
                      {contact.message || "No messages yet"}
                    </div>
                  </div>
                  {contact.highlight && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <span className="w-3 h-3 bg-blue-500 rounded-full block"></span>
                    </div>
                  )}
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Right side - messages */}
        <div className="w-full xl:w-2/3 flex flex-col bg-white max-h-[750px]">
          {currentUserSlug ? (
            <>
              <div className="p-[16px] border-b border-gray-200 flex items-center justify-between">
                <Link
                  to={`/user/profile/${
                    contacts.find((c) => c.user_slug === currentUserSlug)
                      .user_slug
                  }`}
                  className="flex items-center"
                >
                  {contacts.find((c) => c.user_slug === currentUserSlug)
                    ?.user_profile ? (
                    <img
                      src={
                        contacts.find((c) => c.user_slug === currentUserSlug)
                          .user_profile
                      }
                      alt={`Profile of ${
                        contacts.find((c) => c.user_slug === currentUserSlug)
                          .user_name
                      }`}
                      className="w-10 h-10 rounded-full mr-3"
                      onError={(e) => {
                        e.target.src = DEFAULT_PROFILE_IMAGE;
                      }}
                    />
                  ) : (
                    <div
                      style={getColorFromName(
                        contacts.find((c) => c.user_slug === currentUserSlug)
                          ?.user_name || "?"
                      )}
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3"
                    >
                      {(
                        contacts.find((c) => c.user_slug === currentUserSlug)
                          ?.user_name || "?"
                      )
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-bold text-blue-500">
                      {
                        contacts.find((c) => c.user_slug === currentUserSlug)
                          ?.user_name
                      }
                    </div>
                    <div className="text-sm text-gray-500">
                      {contacts.find((c) => c.user_slug === currentUserSlug)
                        ?.isOnline
                        ? "Online"
                        : `Last seen ${getTimeAgo(
                            contacts.find(
                              (c) => c.user_slug === currentUserSlug
                            ).last_seen
                          )} ago`}
                    </div>
                  </div>
                </Link>
                {isPolling && (
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                )}
              </div>
              <div className="flex-1 p-4 max-h-[356px] xl:max-h-[500px] overflow-hidden">
                <div
                  className="h-full overflow-y-auto scroll-smooth"
                  ref={listRef}
                >
                  {isLoadingMessages && (
                    <div className="flex justify-center py-4">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  {!isAtBottomRef.current && (
                    <button
                      onClick={() => scrollToBottom("smooth")}
                      className="fixed bottom-8 right-4  bg-blue-500 text-white rounded-full px-3 py-2 shadow-lg z-10"
                    >
                      <i className="fas fa-arrow-down mr-1"></i> 
                    </button>
                  )}
                  {currentMessages.map((message) => (
                    <div key={message.id}>
                      <Message
                        message={message}
                        isOwn={message.isOwn}
                        openImageModal={openImageModal}
                      />
                    </div>
                  ))}
                  {/* <div ref={messagesEndRef} /> */}
                  {!hasMoreMessages && currentMessages.length > 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      Beginning of conversation
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex flex-col gap-2 relative bg-gray-50">
                <div className="flex items-center relative">
                  <button
                    onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                    className="mr-2 text-gray-600 hover:text-gray-800"
                    title="Attach"
                  >
                    <i className="fas fa-paperclip text-lg"></i>
                  </button>
                  {showAttachmentMenu && (
                    <div
                      ref={attachmentMenuRef}
                      className="absolute bottom-12 left-0 bg-white border rounded-lg shadow-lg z-50"
                    >
                      <button
                        onClick={() => fileInputRef.current.click()}
                        className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-100"
                      >
                        <i className="fas fa-file-alt text-purple-500 mr-2"></i>
                        Document
                      </button>
                      <button
                        onClick={() => galleryInputRef.current.click()}
                        className="flex items-center w-full px-4 py-2 text-left hover:bg-gray-100"
                      >
                        <i className="fas fa-image text-green-500 mr-2"></i>
                        Gallery
                      </button>
                    </div>
                  )}
                  <div className="flex flex-col relative w-full bg-white border rounded-lg p-2">
                    {selectedFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2 pt-2 pl-2">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="relative ">
                            {file.isImage ? (
                              <img
                                src={file.url}
                                alt={file.name}
                                className="w-16 h-16 object-contain rounded"
                              />
                            ) : file.isVideo ? (
                              <video
                                src={file.url}
                                className="w-16 h-16 object-contain rounded"
                                controls
                              />
                            ) : (
                              <div className="w-16 h-16 flex items-center justify-center bg-gray-100">
                                <i className="fas fa-file-alt text-gray-600 text-2xl"></i>
                              </div>
                            )}
                            <button
                              onClick={() => removeFile(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center"
                              title="Remove file"
                            >
                              <i className="fas fa-times text-xs"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <textarea
                      ref={textareaRef}
                      aria-label="Message input"
                      role="textbox"
                      tabIndex={0}
                      rows={1}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (newMessage.trim() || selectedFiles.length > 0) {
                            handleSendMessage();
                          }
                        }
                      }}
                      placeholder="Type a message or press Enter to send..."
                      className="w-full p-2 resize-none border-none outline-none text-base sm:text-sm"
                    />
                  </div>

                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="ml-2 text-gray-600 hover:text-gray-800"
                    title="Toggle emoji picker"
                  >
                    <i className="far fa-smile text-lg"></i>
                  </button>
                  <button
                    onClick={handleSendMessage}
                    className="ml-2 bg-blue-600 w-10 h-10 rounded-full p-2 flex items-center justify-center disabled:opacity-50"
                    disabled={!newMessage.trim() && !selectedFiles.length}
                    title="Send message"
                  >
                    <i className="fas fa-paper-plane text-white"></i>
                  </button>
                </div>
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className="absolute bottom-14 right-2 z-50"
                  >
                    <EmojiPicker
                      onEmojiClick={(emojiObject) => {
                        setNewMessage((prev) => prev + emojiObject.emoji);
                        setShowEmojiPicker(false);
                        setTimeout(() => {
                          if (textareaRef.current) {
                            textareaRef.current.focus();
                          }
                        }, 100);
                      }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  multiple
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx"
                />
                <input
                  type="file"
                  multiple
                  hidden
                  ref={galleryInputRef}
                  onChange={handleFileUpload}
                  accept="image/*,video/*"
                />
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              Select a contact to start chatting
            </div>
          )}
        </div>
      </div>
      {imageModalData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in"
          tabIndex={-1}
        >
          <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden w-[1000px] flex flex-col transition-all duration-300">
            {/* Top Header Section */}
            <div className="w-full flex justify-end p-4 border-b border-gray-200 bg-white z-10">
              <button
                onClick={closeImageModal}
                className="bg-white hover:bg-gray-100 text-black rounded-full w-10 h-10 flex items-center justify-center shadow-md transition-transform duration-200 hover:scale-110"
                title="Close"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            {/* Fixed Size Image/Video Container */}
            <div className="relative w-full h-[600px] bg-black flex items-center justify-center">
              {imageModalData[currentImageIndex].isImage ? (
                <img
                  src={imageModalData[currentImageIndex].url}
                  alt={imageModalData[currentImageIndex].name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <video
                  src={imageModalData[currentImageIndex].url}
                  controls
                  className="w-full h-full object-contain"
                />
              )}

              {/* Navigation Buttons */}
              {imageModalData.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-full w-10 h-10 flex items-center justify-center shadow-md transition-transform duration-200 hover:scale-110"
                    title="Previous"
                  >
                    <i className="fas fa-chevron-left text-xl"></i>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-full w-10 h-10 flex items-center justify-center shadow-md transition-transform duration-200 hover:scale-110"
                    title="Next"
                  >
                    <i className="fas fa-chevron-right text-xl"></i>
                  </button>

                  {/* Counter */}
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-white px-3 py-1 rounded-full shadow text-sm text-gray-700">
                    {currentImageIndex + 1} / {imageModalData.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messaging;
